import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, resolveCharIdFromJwt } from '@/lib/axios';

function resolveNotificationCharId(override?: number): number {
  if (override != null && override > 0) return override;
  return resolveCharIdFromJwt();
}

export interface InboxNotification {
  id: number;
  notification_id: string;
  title: string;
  type_id: number;
  icon_class: string | null;
  icon_url: string | null;
  banner_url: string | null;
  content: string | null;
  content_format?: string;
  isactive: number;
  publish_at: string;
  expires_at: string | null;
  type_name: string | null;
  is_read: number | null;
  read_at: string | null;
  liked: number | null;
}

export function notificationsListQueryKey(charId: number): (string | number)[] {
  return ['notifications', charId];
}

export function notificationDetailQueryKey(charId: number, id: number): (string | number)[] {
  return ['notifications', charId, id];
}

export function unreadNotificationsCountQueryKey(charId: number): (string | number)[] {
  return ['notifications', 'unread-count', charId];
}

async function fetchNotifications(): Promise<InboxNotification[]> {
  const { data } = await api.get<{ status: string; data: InboxNotification[] }>(
    '/api/v1/notifications',
  );
  return data.data ?? [];
}

async function fetchNotificationDetail(id: number): Promise<InboxNotification> {
  const { data } = await api.get<{ status: string; data: InboxNotification }>(
    `/api/v1/notifications/${id}`,
  );
  return data.data;
}

async function fetchUnreadNotificationCount(): Promise<number> {
  const { data } = await api.get<{ status: string; data: { count: number } }>(
    '/api/v1/notifications/unread-count',
  );
  return data.data?.count ?? 0;
}

export function useNotifications() {
  const charId = resolveNotificationCharId();
  return useQuery({
    queryKey: notificationsListQueryKey(charId),
    queryFn: fetchNotifications,
    enabled: charId > 0,
    staleTime: 30_000,
  });
}

export function useNotification(id: number | null) {
  const charId = resolveNotificationCharId();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: notificationDetailQueryKey(charId, id ?? 0),
    queryFn: () => fetchNotificationDetail(id!),
    enabled: charId > 0 && id !== null && id > 0,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.isSuccess && id) {
      void queryClient.invalidateQueries({ queryKey: notificationsListQueryKey(charId) });
      void queryClient.invalidateQueries({ queryKey: unreadNotificationsCountQueryKey(charId) });
    }
  }, [query.isSuccess, query.dataUpdatedAt, charId, id, queryClient]);

  return query;
}

export function useUnreadNotificationCount(charIdOverride?: number) {
  const charId = resolveNotificationCharId(charIdOverride);
  return useQuery({
    queryKey: unreadNotificationsCountQueryKey(charId),
    queryFn: fetchUnreadNotificationCount,
    enabled: charId > 0,
    staleTime: 15_000,
    refetchOnMount: 'always',
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const charId = resolveNotificationCharId();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/api/v1/notifications/${id}/read`);
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: notificationsListQueryKey(charId) });
      void queryClient.invalidateQueries({ queryKey: unreadNotificationsCountQueryKey(charId) });
      void queryClient.invalidateQueries({ queryKey: notificationDetailQueryKey(charId, id) });
    },
  });
}

export function useToggleNotificationLike() {
  const queryClient = useQueryClient();
  const charId = resolveNotificationCharId();

  return useMutation({
    mutationFn: async (id: number): Promise<number | null> => {
      const { data } = await api.post<{ success: boolean; liked: number | null }>(
        `/api/v1/notifications/${id}/like`,
      );
      return data.liked ?? null;
    },
    onSuccess: (_liked, id) => {
      void queryClient.invalidateQueries({ queryKey: notificationsListQueryKey(charId) });
      void queryClient.invalidateQueries({ queryKey: unreadNotificationsCountQueryKey(charId) });
      void queryClient.invalidateQueries({ queryKey: notificationDetailQueryKey(charId, id) });
    },
  });
}

export function prefetchNotificationsQueries(queryClient: QueryClient, charId: number) {
  if (charId <= 0) return;
  void queryClient.prefetchQuery({
    queryKey: notificationsListQueryKey(charId),
    queryFn: fetchNotifications,
    staleTime: 30_000,
  });
  void queryClient.prefetchQuery({
    queryKey: unreadNotificationsCountQueryKey(charId),
    queryFn: fetchUnreadNotificationCount,
    staleTime: 15_000,
  });
}

export function formatNotificationDate(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function cleanNotificationHtmlClient(html: string): string {
  if (!html) return html;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('[data-start], [data-end]').forEach((el) => {
    el.removeAttribute('data-start');
    el.removeAttribute('data-end');
  });
  return tmp.innerHTML;
}

